import { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Clock, Send, Printer, Filter } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TransferHistoryPage() {
  const { t } = useLanguage();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [company, setCompany] = useState({});
  const printRef = useRef(null);

  useEffect(() => {
    fetchTransfers();
    fetchCompany();
  }, []);

  useEffect(() => {
    if (!transfers.length) { setFiltered([]); return; }
    let result = [...transfers];
    if (dateFrom) result = result.filter(t => t.created_at >= dateFrom);
    if (dateTo) result = result.filter(t => t.created_at <= dateTo + 'T23:59:59');
    setFiltered(result);
  }, [transfers, dateFrom, dateTo]);

  const fetchCompany = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/company`);
      setCompany(res.data);
    } catch {}
  };

  const fetchTransfers = async () => {
    try {
      const response = await api.getTransfers();
      setTransfers(response.data);
    } catch (error) {
      console.error('Failed to fetch transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => { setDateFrom(''); setDateTo(''); };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t('print_title_history')}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, 'Hiragino Kaku Gothic ProN', sans-serif; padding: 24px; color: #1a1a1a; font-size: 12px; }
  .header { text-align: center; border-bottom: 2px solid #1a1a1a; padding-bottom: 16px; margin-bottom: 20px; }
  .header h1 { font-size: 20px; font-weight: 700; }
  .header p { font-size: 11px; color: #666; margin-top: 4px; }
  .period { text-align: center; font-size: 11px; color: #666; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  th { background: #f5f5f5; border: 1px solid #ddd; padding: 8px 6px; text-align: left; font-size: 10px; font-weight: 600; }
  td { border: 1px solid #ddd; padding: 7px 6px; font-size: 11px; }
  tr:nth-child(even) { background: #fafafa; }
  .num { text-align: right; font-family: monospace; }
  .footer { text-align: center; font-size: 10px; color: #999; margin-top: 20px; border-top: 1px solid #ddd; padding-top: 12px; }
  .status { padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 500; }
  .status-completed { background: #dcfce7; color: #166534; }
  .status-pending { background: #fef9c3; color: #854d0e; }
  .status-processing { background: #dbeafe; color: #1e40af; }
  .status-failed { background: #fee2e2; color: #991b1b; }
  .status-cancelled { background: #f3f4f6; color: #374151; }
  .summary { display: flex; justify-content: space-between; margin-bottom: 16px; padding: 12px; background: #f9fafb; border-radius: 6px; }
  .summary-item { text-align: center; }
  .summary-label { font-size: 10px; color: #666; }
  .summary-value { font-size: 16px; font-weight: 700; margin-top: 2px; }
  .company-info { text-align: left; font-size: 11px; color: #444; margin-bottom: 16px; padding: 12px 16px; background: #f9fafb; border-radius: 6px; line-height: 1.8; }
  .company-info strong { font-size: 14px; display: block; margin-bottom: 4px; color: #1a1a1a; }
  .company-info .info-row { display: flex; }
  .company-info .info-label { min-width: 110px; color: #888; }
  .company-info .info-value { color: #333; }
  @media print { body { padding: 12px; } }
</style></head><body>`);

    const statusLabel = (s) => ({ pending: t('status_pending'), processing: t('status_processing'), completed: t('status_completed'), failed: t('status_failed'), cancelled: t('status_cancelled') }[s] || s);
    const periodText = dateFrom || dateTo
      ? `${t('print_period')}: ${dateFrom || t('print_no_start')} ～ ${dateTo || t('print_no_end')}`
      : t('print_period_all');

    const totalJPY = filtered.reduce((s, t) => s + (t.total_jpy || 0), 0);
    const totalBRL = filtered.reduce((s, t) => s + (t.amount_brl || 0), 0);
    const totalFee = filtered.reduce((s, t) => s + (t.fee_jpy || 0), 0);

    const c = company;
    const companyName = c.company_name || 'PRC Remit';
    const companyBlock = `<div class="company-info">
      <strong>${companyName}</strong>
      ${c.representative ? `<div class="info-row"><span class="info-label">${t('print_representative')}</span><span class="info-value">${c.representative}</span></div>` : ''}
      ${c.address ? `<div class="info-row"><span class="info-label">${t('print_address')}</span><span class="info-value">${c.address}</span></div>` : ''}
      ${c.phone ? `<div class="info-row"><span class="info-label">${t('print_phone')}</span><span class="info-value">${c.phone}</span></div>` : ''}
      ${c.email ? `<div class="info-row"><span class="info-label">${t('print_email')}</span><span class="info-value">${c.email}</span></div>` : ''}
      <div class="info-row"><span class="info-label">${t('print_website')}</span><span class="info-value">https://prcremit.com</span></div>
    </div>`;

    win.document.write(`
      <div class="header"><h1>${t('print_title_history')}</h1><p>${t('print_date')}: ${new Date().toLocaleDateString('ja-JP')}</p></div>
      ${companyBlock}
      <div class="period">${periodText} (${filtered.length}${t('print_items')})</div>
      <div class="summary">
        <div class="summary-item"><div class="summary-label">${t('print_total_jpy')}</div><div class="summary-value">¥${totalJPY.toLocaleString()}</div></div>
        <div class="summary-item"><div class="summary-label">${t('print_total_brl')}</div><div class="summary-value">R$ ${totalBRL.toLocaleString()}</div></div>
        <div class="summary-item"><div class="summary-label">${t('print_total_fee')}</div><div class="summary-value">¥${totalFee.toLocaleString()}</div></div>
      </div>
      <table><thead><tr>
        <th>${t('print_col_no')}</th><th>${t('print_col_date')}</th><th>${t('print_col_recipient')}</th><th>${t('print_col_amount_jpy')}</th><th>${t('print_col_fee')}</th><th>${t('print_col_total_jpy')}</th><th>${t('print_col_amount_brl')}</th><th>${t('print_col_rate')}</th><th>${t('print_col_status')}</th><th>${t('print_col_id')}</th>
      </tr></thead><tbody>`);

    filtered.forEach((tr, i) => {
      win.document.write(`<tr>
        <td>${i + 1}</td>
        <td>${new Date(tr.created_at).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
        <td>${tr.recipient?.full_name || tr.recipient_name || '-'}</td>
        <td class="num">¥${(tr.amount_jpy || 0).toLocaleString()}</td>
        <td class="num">¥${(tr.fee_jpy || 0).toLocaleString()}</td>
        <td class="num">¥${(tr.total_jpy || 0).toLocaleString()}</td>
        <td class="num">R$ ${(tr.amount_brl || 0).toLocaleString()}</td>
        <td class="num">${(tr.exchange_rate || 0).toFixed(5)}</td>
        <td><span class="status status-${tr.status}">${statusLabel(tr.status)}</span></td>
        <td style="font-family:monospace;font-size:9px;">${tr.id.substring(0, 13)}</td>
      </tr>`);
    });

    win.document.write(`</tbody></table>
      <div class="footer">${companyName} | prcremit.com${c.phone ? ' | TEL: ' + c.phone : ''}${c.license_number ? ` | ${t('print_license')}: ` + c.license_number : ''} | ${t('print_footer_user')}</div>
    </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: t('status_pending'), className: 'bg-yellow-500/15 text-yellow-700 border-yellow-500/20' },
      processing: { label: t('status_processing'), className: 'bg-blue-500/15 text-blue-700 border-blue-500/20' },
      completed: { label: t('status_completed'), className: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/20' },
      failed: { label: t('status_failed'), className: 'bg-red-500/15 text-red-700 border-red-500/20' },
      cancelled: { label: t('status_cancelled'), className: 'bg-gray-500/15 text-gray-700 border-gray-500/20' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.className}`}>
        {config.label}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div data-testid="transfer-history-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-primary mb-1">{t('history_title')}</h1>
            <p className="text-muted-foreground text-sm">{t('history_subtitle')}</p>
          </div>
          {filtered.length > 0 && (
            <Button variant="outline" size="sm" onClick={handlePrint} className="flex-shrink-0" data-testid="print-history-button">
              <Printer className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">{t('print')}</span>
            </Button>
          )}
        </div>

        {/* Date Filter */}
        <div className="bg-card border border-border/50 rounded-xl p-3 mb-4" data-testid="date-filter">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Filter className="h-3.5 w-3.5" />期間指定
            {(dateFrom || dateTo) && (
              <button onClick={clearFilter} className="ml-auto text-accent text-xs" data-testid="clear-filter-button">{t('clear_filter')}</button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="text-sm h-9" data-testid="date-from-input" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="text-sm h-9" data-testid="date-to-input" />
          </div>
          {(dateFrom || dateTo) && (
            <div className="text-xs text-muted-foreground mt-1.5">{filtered.length}{t('admin_transfers_count_records')}</div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Clock className="h-16 w-16 text-muted-foreground mx-auto mb-6 opacity-50" />
            <h3 className="font-heading text-xl font-semibold text-primary mb-2">{t('history_empty_title')}</h3>
            <p className="text-muted-foreground">{t('history_empty_desc')}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="transfers-list" ref={printRef}>
            {filtered.map((transfer) => (
              <div
                key={transfer.id}
                className="bg-card border border-border/50 rounded-xl p-4 hover:shadow-md smooth-transition"
                data-testid={`transfer-item-${transfer.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Send className="h-5 w-5 text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">
                        {transfer.recipient?.full_name || transfer.recipient_name || ''}
                      </span>
                      {getStatusBadge(transfer.status)}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-heading text-base font-semibold text-primary">
                        ¥{transfer.total_jpy.toLocaleString()}
                      </span>
                      <span className="text-muted-foreground text-xs">→</span>
                      <span className="font-heading text-base font-semibold text-accent">
                        R$ {transfer.amount_brl.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(transfer.created_at).toLocaleString('ja-JP', {
                        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-muted-foreground text-xs">{t('send_amount')}</div>
                    <div className="font-medium font-mono text-sm">¥{transfer.amount_jpy.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">{t('send_fee')}</div>
                    <div className="font-medium font-mono text-sm">¥{transfer.fee_jpy.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">{t('send_rate')}</div>
                    <div className="font-medium font-mono text-sm">{transfer.exchange_rate.toFixed(5)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs">{t('history_payment_status')}</div>
                    <div className="font-medium text-sm">
                      {transfer.payment_status === 'awaiting' && t('payment_awaiting')}
                      {transfer.payment_status === 'paid' && t('payment_paid')}
                      {transfer.payment_status === 'confirmed' && t('payment_confirmed')}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
